import { memo, useCallback, useEffect, useRef, useState } from 'react';

import css from 'src/utils/css';

export const FORMULARIO_GLOBAL_IMAGE_SIZE = {
  MIN_WIDTH: 80,
  MIN_HEIGHT: 40,
  MAX_WIDTH: 2000,
  MAX_HEIGHT: 800,
};

const hasImageDimension = (value) =>
  value !== null && value !== undefined && value !== '';

const normalizeImageDimension = (value, min, max) => {
  const numericValue = Number(value);

  if (!hasImageDimension(value) || Number.isNaN(numericValue)) return null;

  return Math.min(Math.max(numericValue, min), max);
};

const getNaturalImageSize = (naturalWidth, naturalHeight) => {
  if (!naturalWidth || !naturalHeight) return { width: null, height: null };

  const scale = Math.min(
    FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_WIDTH / naturalWidth,
    FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_HEIGHT / naturalHeight,
    1
  );

  return {
    width: Math.max(
      Math.round(naturalWidth * scale),
      FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_WIDTH
    ),
    height: Math.max(
      Math.round(naturalHeight * scale),
      FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_HEIGHT
    ),
  };
};

const classes = css`
  .imageFrame {
    align-items: center;
    background: #f8fafc;
    border: 1px solid #dbe3ea;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    margin-top: 14px;
    max-height: ${FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_HEIGHT}px;
    max-width: 100%;
    min-height: ${FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_HEIGHT}px;
    min-width: ${FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_WIDTH}px;
    overflow: hidden;
    resize: both;
  }

  .imageFrame img {
    display: block;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
    width: 100%;
  }

  .imageFrameDisabled {
    resize: none;
  }
`;

export const clampImageSize = (width, height) => ({
  width: normalizeImageDimension(
    width,
    FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_WIDTH,
    FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_WIDTH
  ),
  height: normalizeImageDimension(
    height,
    FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_HEIGHT,
    FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_HEIGHT
  ),
});

export const normalizeStoredImageSize = clampImageSize;

const FormularioGlobalResizableImage = ({
  alt,
  disabled,
  height,
  onResize,
  src,
  width,
}) => {
  const frameRef = useRef(null);
  const isResizingRef = useRef(false);
  const hasResizedRef = useRef(false);
  const lastCommittedSizeRef = useRef({ width: null, height: null });
  const [naturalSize, setNaturalSize] = useState({
    width: null,
    height: null,
  });
  const configuredSize = clampImageSize(width, height);
  const imageSize = {
    width: configuredSize.width || naturalSize.width,
    height: configuredSize.height || naturalSize.height,
  };

  const commitResize = useCallback(() => {
    if (disabled || !frameRef.current || !onResize) return;

    const rect = frameRef.current.getBoundingClientRect();
    const nextSize = clampImageSize(
      Math.round(rect.width),
      Math.round(rect.height)
    );

    if (
      nextSize.width === lastCommittedSizeRef.current.width &&
      nextSize.height === lastCommittedSizeRef.current.height
    ) {
      return;
    }

    lastCommittedSizeRef.current = nextSize;
    onResize(nextSize);
  }, [disabled, onResize]);

  useEffect(() => {
    if (isResizingRef.current) return;

    lastCommittedSizeRef.current = imageSize;
  }, [imageSize.height, imageSize.width]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || disabled) return undefined;

    const handleResizeStart = () => {
      isResizingRef.current = true;
      hasResizedRef.current = false;
    };
    const handleResizeEnd = () => {
      if (!isResizingRef.current) return;

      isResizingRef.current = false;
      if (hasResizedRef.current) {
        commitResize();
        hasResizedRef.current = false;
      }
    };
    const resizeObserver = new ResizeObserver(() => {
      if (isResizingRef.current) {
        hasResizedRef.current = true;
      }
    });

    resizeObserver.observe(frame);
    frame.addEventListener('pointerdown', handleResizeStart);
    window.addEventListener('pointerup', handleResizeEnd);
    window.addEventListener('blur', handleResizeEnd);

    return () => {
      resizeObserver.disconnect();
      frame.removeEventListener('pointerdown', handleResizeStart);
      window.removeEventListener('pointerup', handleResizeEnd);
      window.removeEventListener('blur', handleResizeEnd);
    };
  }, [commitResize, disabled]);

  return (
    <div
      className={`${classes.imageFrame} ${
        disabled ? classes.imageFrameDisabled : ''
      }`}
      ref={frameRef}
      style={{
        height: imageSize.height || undefined,
        width: imageSize.width || undefined,
      }}
    >
      <img
        alt={alt}
        onLoad={(event) =>
          setNaturalSize(
            getNaturalImageSize(
              event.currentTarget.naturalWidth,
              event.currentTarget.naturalHeight
            )
          )
        }
        src={src}
      />
    </div>
  );
};

export default memo(FormularioGlobalResizableImage);
